import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { errMsg, newId, useApi } from "@/lib/api";

export const EditorCtx = createContext(null);
export const useEditor = () => useContext(EditorCtx);

const clone = (x) => structuredClone(x);
export const slugify = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9:/]+/g, "-").replace(/^-+|-+$/g, "") || "pagina";

export function findSection(content, id) {
  for (const page of content?.pages || []) {
    const index = page.sections.findIndex((s) => s.id === id);
    if (index >= 0) return { page, index, section: page.sections[index] };
  }
  return null;
}
export const pagePath = (content, page) => (page.id === content.home_page_id ? "/" : `/${String(page.slug).replace(/^\/+/, "")}`);

function uniqueSlug(content, base, exceptId) {
  let slug = slugify(base), i = 2;
  while (content.pages.some((p) => p.slug === slug && p.id !== exceptId)) slug = `${slugify(base)}-${i++}`;
  return slug;
}
const move = (arr, from, to) => { const [x] = arr.splice(from, 1); arr.splice(to, 0, x); };
const HISTORY = 80;
const COALESCE_MS = 800;

export function useEditorState(id) {
  const api = useApi();
  const [project, setProject] = useState(null);
  const [content, setContent] = useState(null);
  const [assets, setAssets] = useState([]);
  const [saveState, setSaveState] = useState("saved");
  const [pageId, setPageId] = useState(null);
  const [selection, setSelection] = useState(null);
  const [diagnostics, setDiagnostics] = useState([]);
  const [hist, setHist] = useState({ undo: 0, redo: 0 });
  const [aiPreview, setAiPreview] = useState(null);
  const [picked, setPicked] = useState(null);
  const r = useRef({ content: null, dirty: false, timer: null, chain: Promise.resolve(), past: [], future: [], lastPush: 0, lastKey: null });
  const syncHist = () => setHist({ undo: r.current.past.length, redo: r.current.future.length });

  const replaceContent = useCallback((c, { keepHistory = false } = {}) => {
    if (keepHistory && r.current.content) { r.current.past.push(r.current.content); r.current.past = r.current.past.slice(-HISTORY); r.current.future = []; }
    else if (!keepHistory) { r.current.past = []; r.current.future = []; }
    syncHist();
    r.current.content = c; r.current.dirty = false;
    setContent(c); setSaveState("saved");
    setPageId((cur) => (c.pages.some((x) => x.id === cur) ? cur : c.home_page_id || c.pages[0]?.id));
  }, []);

  const load = useCallback(async () => {
    const [{ data: p }, { data: a }] = await Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/assets`)]);
    const { content: c, ...meta } = p;
    setProject(meta); setAssets(a); replaceContent(c);
    return p;
  }, [api, id, replaceContent]);

  const doSave = useCallback(async () => {
    const st = r.current;
    if (!st.dirty || !st.content) return true;
    st.dirty = false; setSaveState("saving");
    try {
      await api.put(`/projects/${id}/content`, { content: st.content });
      setSaveState(st.dirty ? "dirty" : "saved");
      setProject((p) => ({ ...p, updated_at: new Date().toISOString() }));
      return true;
    } catch (e) {
      st.dirty = true; setSaveState("error");
      toast.error(`Salvataggio non riuscito: ${errMsg(e)}`);
      return false;
    }
  }, [api, id]);

  const save = useCallback(() => {
    clearTimeout(r.current.timer);
    r.current.chain = r.current.chain.then(doSave);
    return r.current.chain;
  }, [doSave]);

  const commit = useCallback((next) => {
    r.current.content = next; r.current.dirty = true;
    setContent(next); setSaveState("dirty");
    clearTimeout(r.current.timer);
    r.current.timer = setTimeout(save, 1500);
  }, [save]);

  // key: only consecutive edits of the same field (e.g. typing) merge into one undo step
  const update = useCallback((mutator, key = null) => {
    const st = r.current, prev = st.content;
    const next = clone(prev);
    mutator(next);
    const now = Date.now();
    const merge = key && key === st.lastKey && now - st.lastPush <= COALESCE_MS && st.past.length;
    if (!merge) { st.past.push(prev); st.past = st.past.slice(-HISTORY); }
    st.lastPush = now; st.lastKey = key; st.future = [];
    syncHist();
    commit(next);
  }, [commit]);

  const undo = useCallback(() => {
    const st = r.current;
    if (!st.past.length) return false;
    st.future.push(st.content); st.lastPush = 0; st.lastKey = null;
    commit(st.past.pop()); syncHist();
    return true;
  }, [commit]);
  const redo = useCallback(() => {
    const st = r.current;
    if (!st.future.length) return false;
    st.past.push(st.content); st.lastPush = 0; st.lastKey = null;
    commit(st.future.pop()); syncHist();
    return true;
  }, [commit]);

  useEffect(() => {
    const h = (e) => { if (r.current.dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => { window.removeEventListener("beforeunload", h); clearTimeout(r.current.timer); };
  }, []);

  const actions = useMemo(() => ({
    addPage(name = "Nuova pagina") {
      const pid = newId("pg");
      update((c) => { c.pages.push({ id: pid, name, slug: uniqueSlug(c, name), in_nav: true, nav_label: "", sections: [] }); });
      return pid;
    },
    updatePage(pid, patch) {
      update((c) => {
        const p = c.pages.find((x) => x.id === pid);
        const followName = patch.name !== undefined && patch.slug === undefined && new RegExp(`^${slugify(p.name)}(-\\d+)?$`).test(p.slug);
        Object.assign(p, patch);
        if (followName) p.slug = uniqueSlug(c, patch.name, pid);
        if (patch.slug !== undefined) p.slug = uniqueSlug(c, patch.slug || p.name, pid);
      }, `page:${pid}:${Object.keys(patch).join()}`);
    },
    deletePage(pid) {
      update((c) => {
        c.pages = c.pages.filter((x) => x.id !== pid);
        if (c.home_page_id === pid) c.home_page_id = c.pages[0]?.id || null;
      });
    },
    duplicatePage(pid) {
      const nid = newId("pg");
      update((c) => {
        const src = c.pages.find((x) => x.id === pid);
        const copy = clone(src);
        Object.assign(copy, { id: nid, name: `${src.name} (copia)`, slug: uniqueSlug(c, `${src.slug}-copia`) });
        copy.sections = copy.sections.map((s) => ({ ...s, id: newId("sec") }));
        c.pages.splice(c.pages.indexOf(src) + 1, 0, copy);
      });
      return nid;
    },
    setHome(pid) { update((c) => { c.home_page_id = pid; }); },
    movePage(from, to) { update((c) => move(c.pages, from, to)); },
    addSection(pid, section, index) {
      const sec = { id: newId("sec"), name: "Sezione", type: "react", code: "", html: "", css: "", js: "", props: {}, style: {}, hidden: false, origin: "manual", ...section };
      update((c) => { const p = c.pages.find((x) => x.id === pid); p.sections.splice(index ?? p.sections.length, 0, sec); });
      return sec.id;
    },
    updateSection(sid, patch) { update((c) => { Object.assign(findSection(c, sid).section, patch); }, `sec:${sid}:${Object.keys(patch).join()}`); },
    updateSectionStyle(sid, patch) { update((c) => { const s = findSection(c, sid).section; s.style = { ...(s.style || {}), ...patch }; }, `style:${sid}:${Object.keys(patch).join()}`); },
    duplicateSection(sid) {
      const nid = newId("sec");
      update((c) => { const f = findSection(c, sid); f.page.sections.splice(f.index + 1, 0, { ...clone(f.section), id: nid, name: `${f.section.name} (copia)` }); });
      return nid;
    },
    deleteSection(sid) { update((c) => { const f = findSection(c, sid); f.page.sections.splice(f.index, 1); }); },
    moveSection(pid, from, to) { update((c) => move(c.pages.find((x) => x.id === pid).sections, from, to)); },
    addComponent(comp) {
      const cid = newId("cmp");
      update((c) => { c.components.push({ id: cid, placement: "none", origin: "custom", code: "", ...comp }); });
      return cid;
    },
    updateComponent(cid, patch) { update((c) => { Object.assign(c.components.find((x) => x.id === cid), patch); }, `cmp:${cid}:${Object.keys(patch).join()}`); },
    deleteComponent(cid) { update((c) => { c.components = c.components.filter((x) => x.id !== cid); }); },
    duplicateComponent(cid) {
      const nid = newId("cmp");
      update((c) => { const src = c.components.find((x) => x.id === cid); c.components.push({ ...clone(src), id: nid, name: `${src.name}Copia`, placement: "none" }); });
      return nid;
    },
    updateDesign(fn, key = "design") { update((c) => fn(c.design), key); },
    setData(d) { update((c) => { c.data = d; }, "data"); },
    setGlobalCss(css) { update((c) => { c.global_css = css; }, "global_css"); },
    setNav(patch) { update((c) => { c.nav = { ...(c.nav || {}), ...patch }; }, `nav:${Object.keys(patch).join()}`); },
    setWhatsapp(patch) { update((c) => { c.whatsapp = { ...(c.whatsapp || {}), ...patch }; }, `wa:${Object.keys(patch).join()}`); },
    setFavicon(next) { update((c) => { c.favicon = next; }, "favicon"); },
    setLanguage(l) { update((c) => { c.language = l; }); },
    // replaces every use of an image URL (code, data, props) in one undo step; returns the number of replacements
    replaceImage(from, to) {
      let n = 0;
      const a = JSON.stringify(from).slice(1, -1), b = JSON.stringify(to).slice(1, -1);
      update((c) => {
        const raw = JSON.stringify(c);
        n = raw.split(a).length - 1;
        const next = JSON.parse(raw.split(a).join(b));
        Object.keys(c).forEach((k) => delete c[k]);
        Object.assign(c, next);
      });
      return n;
    },
    toggleComponentOnPage(pid, cid, visible) {
      update((c) => {
        const p = c.pages.find((x) => x.id === pid);
        const set = new Set(p.hide_components || []);
        if (visible) set.delete(cid); else set.add(cid);
        p.hide_components = [...set];
      });
    },
    detachComponent(cid, pid) {
      const sid = newId("sec");
      update((c) => {
        const comp = c.components.find((x) => x.id === cid);
        const p = c.pages.find((x) => x.id === pid);
        p.hide_components = [...new Set([...(p.hide_components || []), cid])];
        const slot = comp.placement === "footer" ? "footer" : "header";
        const sec = { id: sid, name: `${comp.name} (solo questa pagina)`, type: "react", code: comp.code, css: comp.css || "", html: "", js: "", props: {}, style: {}, hidden: false, origin: "custom", slot };
        if (comp.placement === "footer") p.sections.push(sec); else p.sections.unshift(sec);
      });
      return sid;
    },
  }), [update]);

  return { id, api, project, setProject, content, assets, setAssets, saveState, save, load, replaceContent, pageId, setPageId,
    selection, setSelection, diagnostics, setDiagnostics, aiPreview, setAiPreview, picked, setPicked, undo, redo, canUndo: hist.undo > 0, canRedo: hist.redo > 0, ...actions };
}
